import React, { useState, useEffect } from "react";
import { Select, MenuItem, SelectChangeEvent, Box } from "@mui/material";

const StatusChip = ({
  initialStatus,
  onChange,
  disabled = false,
}: {
  initialStatus: string;
  onChange: (newStatus: string) => Promise<void> | void;
  disabled?: boolean;
}) => {
  const [status, setStatus] = useState(initialStatus);
  const [isChanging, setIsChanging] = useState(false);

  const statusColors: Record<
    string,
    { bg: string; text: string; border: string }
  > = {
    Draft: { bg: "#e3f2fd", text: "#0d6efd", border: "#0d6efd" },
    Won: { bg: "#e8f5e8", text: "#198754", border: "#198754" },
    Lost: { bg: "#ffebee", text: "#dc3545", border: "#dc3545" },
    Sent: { bg: "#fff8e1", text: "#ffc107", border: "#ffc107" },
    "PI Generated": { bg: "#e8f5e8", text: "#198754", border: "#198754" },
  };

  // Update local status when initialStatus prop changes
  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    if (disabled || isChanging) return;

    const newStatus = event.target.value;
    const previousStatus = status;

    // Optimistically update the UI
    setStatus(newStatus);
    setIsChanging(true);

    try {
      // Call the onChange function (which should make the API call)
      await onChange(newStatus);
    } catch (error) {
      // If API call fails, revert to previous status
      setStatus(previousStatus);
      console.error("Failed to update status:", error);
    } finally {
      setIsChanging(false);
    }
  };

  // Available status options for the dropdown (PI Generated is excluded from dropdown but can be displayed)
  const availableStatusOptions = ["Draft", "Won", "Lost", "Sent"];

  const ITEM_WIDTH = status === "PI Generated" ? 110 : 70;

  return (
    <Select
      value={status}
      onChange={handleChange}
      size="small"
      variant="outlined"
      disabled={disabled || isChanging}
      // Allow value that's not in options (for "PI Generated" display)
      renderValue={(value) => value}
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 200,
            padding: "8px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "1px solid #e0e0e0",
          },
        },
      }}
      sx={{
        width: ITEM_WIDTH,
        fontSize: "0.7rem",
        borderRadius: "16px",
        border: `1px solid ${statusColors[status]?.border}`,
        backgroundColor: statusColors[status]?.bg,
        color: statusColors[status]?.text,
        fontWeight: 600,
        cursor: disabled || isChanging ? "not-allowed !important" : "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: disabled || isChanging ? "none" : "translateY(-1px)",
          boxShadow:
            disabled || isChanging ? "none" : "0 2px 6px rgba(0,0,0,0.1)",
        },
        "&.Mui-disabled": {
          cursor: "not-allowed !important",
          opacity: 0.6,
          backgroundColor: statusColors[status]?.bg,
          color: statusColors[status]?.text,
          pointerEvents: "auto !important",
        },
        "&.Mui-disabled .MuiSelect-select": {
          cursor: "not-allowed !important",
        },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          cursor: "not-allowed !important",
          border: `1px solid ${statusColors[status]?.border}`,
        },
        "& .MuiSelect-select": {
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled || isChanging ? "not-allowed !important" : "pointer",
        },
        "& .MuiSvgIcon-root": {
          fontSize: "0.9rem",
          color: statusColors[status]?.text,
          cursor: disabled || isChanging ? "not-allowed !important" : "pointer",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      }}
    >
      {availableStatusOptions.map((option, idx, arr) => (
        <MenuItem
          key={option}
          value={option}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "12px",
            // Remove all padding in MenuItem
            padding: "2px 0px",
            // Option width same as select (force same width)
            minWidth: ITEM_WIDTH,
            width: ITEM_WIDTH,
            boxSizing: "border-box",
            minHeight: 32,
            marginBottom: idx !== arr.length - 1 ? "8px" : 0, // "gap" between options
            backgroundColor: statusColors[option]?.bg,
            color: statusColors[option]?.text,
            fontWeight: 600,
            fontSize: "0.7rem",
            border: `1px solid ${statusColors[option]?.border}`,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              filter: disabled ? "brightness(1)" : "brightness(0.95)",
              transform: disabled ? "scale(1)" : "scale(1.02)",
            },
          }}
        >
          <Box
            sx={{
              // Remove visible padding, fill MenuItem
              padding: 0,
              borderRadius: "8px",
              backgroundColor: "transparent",
              textAlign: "center",
              width: "100%",
              fontWeight: 600,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {option}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default StatusChip;
